<template>
  <div class="min-h-screen">
    <!-- SEO Meta Tags -->
    <PageSEO
      title="Backend Development Services - Server-Side Solutions"
      description="Robust server-side applications, APIs, and database solutions built for scalability, security, and performance. We create the foundation that powers your digital success."
      :keywords="['backend development', 'server-side development', 'API development', 'database design', 'PHP', 'Laravel', 'Node.js', 'scalable architecture']"
      url="https://vedware.com/services/backend-development"
    />

    <!-- Hero Section -->
    <HeroSection
      :icon="PhDatabase"
      title="Backend"
      highlighted-title="Development"
      description="Robust server-side applications, APIs, and database solutions built for scalability, security, and performance. We create the foundation that powers your digital success."
    />

    <!-- Service Overview -->
    <ServiceOverview
      :description="[
        'Our backend development service focuses on creating secure, scalable, and maintainable server-side applications. We build the robust foundation that powers your web applications and ensures optimal performance.',
        'From RESTful APIs to complex database architectures, we design and implement backend solutions that can handle high traffic, ensure data security, and scale with your business growth.',
      ]"
      :features="[
        'Scalable and maintainable architecture',
        'Secure authentication and authorization',
        'Database design and optimization',
        'API development and integration',
        'Performance monitoring and optimization',
        'Automated testing and deployment',
      ]"
    />

    <!-- Technologies Section -->
    <ServiceTechnologies
      title="Backend Technologies"
      description="We leverage proven backend technologies and frameworks to build robust, scalable applications."
      :categories="technologyCategories"
    />

    <!-- Services Offered -->
    <ServiceOfferings
      title="Backend Services"
      description="We provide comprehensive backend development services to power your applications."
      :services="backendServices"
    />

    <ServiceProjects slug="backend-development" />

    <!-- CTA Section -->
    <ServiceCTA
      title="Ready to Build a Robust"
      highlighted-title="Backend?"
      description="Let's create a scalable, secure backend that powers your application and grows with your business."
    />
  </div>
</template>

<script setup lang="ts">
import { ref } from '#imports'
import {
  PhChartLineUp,
  PhCloud,
  PhCode,
  PhDatabase,
  PhGitBranch,
  PhLightning,
  PhShield,
  PhStack,
} from '@phosphor-icons/vue'
import ServiceCTA from '~/components/Services/ServiceCTA.vue'
import ServiceOfferings from '~/components/Services/ServiceOfferings.vue'
import ServiceOverview from '~/components/Services/ServiceOverview.vue'
import ServiceProjects from '~/components/Services/ServiceProjects.vue'
import ServiceTechnologies from '~/components/Services/ServiceTechnologies.vue'
import HeroSection from '~/components/Shared/HeroSection.vue'
import PageSEO from '~/components/Shared/PageSEO.vue'

const technologyCategories = ref([
  {
    title: 'Languages',
    icon: PhCode,
    technologies: [
      'PHP',
      'Node.js',
      'Python',
      'JavaScript',
      'TypeScript',
      'Ruby',
      'OOP',
      'Functional Programming',
    ],
  },
  {
    title: 'Frameworks',
    icon: PhStack,
    technologies: [
      'Laravel',
      'Express.js',
      'Symfony',
      'Ruby on Rails',
      'Kohana',
      'CodeIgniter',
      'Django',
      'Doctrine 2',
    ],
  },
  {
    title: 'Databases',
    icon: PhDatabase,
    technologies: [
      'MySQL',
      'PostgreSQL',
      'MongoDB',
      'Redis',
      'Memcached',
      'SQLite',
      'MariaDB',
      'Microsoft SQL Server',
    ],
  },
])

const backendServices = ref([
  {
    icon: PhLightning,
    title: 'RESTful API Development',
    description: 'Clean, well-documented APIs that enable seamless communication between frontend and backend systems.',
  },
  {
    icon: PhDatabase,
    title: 'Database Design',
    description: 'Optimized database schemas and queries for maximum performance and data integrity.',
  },
  {
    icon: PhShield,
    title: 'Authentication & Security',
    description: 'Secure user authentication, authorization, and data protection using industry best practices.',
  },
  {
    icon: PhChartLineUp,
    title: 'Performance Optimization',
    description: 'Code optimization, caching strategies, and performance monitoring for fast, efficient applications.',
  },
  {
    icon: PhGitBranch,
    title: 'Microservices Architecture',
    description: 'Scalable microservices architecture that allows independent development and deployment.',
  },
  {
    icon: PhCloud,
    title: 'Cloud Integration',
    description: 'Seamless integration with cloud services and platforms for scalable, reliable applications.',
  },
])
</script>
